/** @jsx jsx */
import { css, jsx } from '@emotion/react';
import React from 'react';
import classNames from 'classnames';
import { ILobbyTxId, LobbyTxId } from '../lobby/LobbyCard';

const getStyleByVariant = (
  secondary: boolean | undefined,
  success: boolean | undefined,
  disabled: boolean | undefined,
  inactive: boolean | undefined,
  dataSize: string | undefined
) => [
  css`
    border-radius: 0;
    background-color: #7530e3;
    border-color: #7530e3;
    color: #fff;

    &:active,
    &:focus,
    &:hover {
      background-color: #4d1f96;
      border-color: #4d1f96;
      box-shadow: 0 0 0 0.25rem rgb(37 15 73 / 50%);
    }

    &:active:focus {
      box-shadow: 0 0 0 0.25rem rgb(31 7 68 / 70%);
    }
  `,
  ...(secondary ? styleClassButton.secondary : []),
  ...(disabled ? styleClassButton.disabled : []),
  ...(inactive ? styleClassButton.inactive : []),
  ...(success ? styleClassButton.success : []),
  ...(dataSize ? styleClassButton.sizes[dataSize] : []),
];

const styleClassButton: Record<string, any> = {
  inactive: [
    css`
      &.inactive {
        color: #fff;
        background-color: #321463;
        border-color: #321463;
        display: flex;
        justify-content: space-between;
      }

      &.inactive,
      fieldset:disabled & {
        pointer-events: initial;
        cursor: auto;
        opacity: 0.9;
      }

      &.inactive div {
        margin: 0 -3px;
      }
    `,
  ],
  disabled: [
    css`
      &.disabled,
      &:disabled {
        color: #fff;
        background-color: #321463;
        border-color: #321463;
      }
      &.disabled,
      &:disabled,
      fieldset:disabled & {
        pointer-events: none;
        opacity: 0.9;
      }
    `,
  ],
  secondary: [
    css`
      border-radius: 0;
      background-color: #fdcf00;
      border-color: #fdcf00;

      &:active,
      &:focus,
      &:hover {
        background-color: #fcd732;
        border-color: #fcd732;
        box-shadow: 0 0 0 0.25rem rgb(250 250 45 / 50%);
      }
      &:active:focus {
        box-shadow: 0 0 0 0.25rem rgb(174 174 129 / 50%);
      }
    `,
  ],
  success: [
    css`
      background-color: #1db78c;
      border-color: #1db78c;
      &!.inactive,
      &:active,
      &:focus,
      &:hover {
        background-color: #0fa87d;
        border-color: #0fa87d;
        box-shadow: 0 0 0 0.25rem hwb(163deg 11% 28% / 50%);
      }

      &.disabled,
      &:disabled {
        opacity: 1;
      }

      &.inactive {
        opacity: 1;
      }
    `,
  ],
  sizes: {
    small: [
      css`
        height: 12px;
        line-height: 14px;
        border-radius: 6px;
        &.btn-secondary {
          color: #000;
        }
      `,
    ],
  },
};

export type IButtonProps = {
  label: string;
  onClick?: () => void;
  secondary?: boolean;
  className?: string;
  disabled?: boolean;
  inactive?: boolean;
  success?: boolean;
  dataSize?: string;
  img?: ILobbyTxId;
} & React.HTMLProps<HTMLButtonElement> &
  React.HTMLAttributes<HTMLButtonElement>;

export const Button = (props: IButtonProps) => {
  const { label, secondary, success, className, disabled, inactive, dataSize, img, ...buttonProps } = props;

  const baseClass = 'btn btn-lg px-4 gap-3';
  const finalClass = classNames(baseClass, className, {
    'btn-primary': !secondary,
    'btn-secondary': secondary,
    'btn-success': success,
    disabled: disabled,
    inactive: inactive,
  });

  return (
    <button
      className={finalClass}
      {...buttonProps}
      type={'button'}
      disabled={disabled}
      css={getStyleByVariant(secondary, success, disabled, inactive, dataSize)}
    >
      <div>{label}</div>
      {img ? (
        <div>
          <LobbyTxId txId={img.txId} urlType={img.urlType} imgSrc={img.imgSrc} imgWidth={img.imgWidth} />
        </div>
      ) : (
        ''
      )}
    </button>
  );
};
